import axios, { AxiosResponse } from 'axios';
import { getEnv } from '../env';

/**
 * Replace state with current page path to make sure user can be redirected to current page after login
 * If current URL has state param (from previous redirect), preserve it to maintain original path
 * Otherwise use current window location (first attempt)
 * Always clean id_token to prevent accumulation
 */
const replaceMidwayAuthNRedirectURL = (loginURL: string):string => {
  const url = new URL(loginURL);
  if (url.searchParams.has('state')) {
    const currentUrl = new URL(window.location.href);
    
    // If current URL has state param, use it (contains correct original path)
    if (currentUrl.searchParams.has('state')) {
      const originalState = currentUrl.searchParams.get('state')!;
      url.searchParams.set('state', originalState);
    } else {
      // First attempt: use current window location
      // Clean id_token to prevent accumulation
      currentUrl.searchParams.delete('id_token');
      url.searchParams.set('state', btoa(JSON.stringify({
        path: currentUrl.pathname + currentUrl.search,
      })));
    }
  }
  return url.toString();
};

export const getWebServerAxiosInstance = async () => {
  const env = await getEnv();
  const webServerAxiosInstance = axios.create({
    baseURL: env.serverEndpoint,
    timeout: 25000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  webServerAxiosInstance.interceptors.request.use((config) => config, (error) => Promise.reject(error));

  webServerAxiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const { response, config } = error;
      if (response.status === 401) {
          const { data } = response;
          const { loginURL, authType, sessionId } = data;
          localStorage.setItem("sancus#sessionId", sessionId);
          
          // Check if this is post-authentication (id_token in URL)
          const currentUrl = new URL(window.location.href);
          const hasIdToken = currentUrl.searchParams.has('id_token');
          
          if (hasIdToken) {
            // Post-auth: retry via XHR instead of page reload
            // Extract original path from state parameter
            const stateParam = currentUrl.searchParams.get('state');
            if (stateParam) {
              try {
                const stateData = JSON.parse(atob(stateParam));
                const originalPath = stateData.path || '/';
                window.history.replaceState({}, '', originalPath);
              } catch (e) {
                // If state parsing fails, just clean the URL
                currentUrl.searchParams.delete('id_token');
                currentUrl.searchParams.delete('state');
                window.history.replaceState({}, '', currentUrl.toString());
              }
            } else {
              // No state param, just clean id_token
              currentUrl.searchParams.delete('id_token');
              window.history.replaceState({}, '', currentUrl.toString());
            }
            
            // Retry the original request without id_token and state params
            const retryUrl = new URL(config.url, window.location.origin);
            retryUrl.searchParams.delete('id_token');
            retryUrl.searchParams.delete('state');
            return webServerAxiosInstance.request({
              ...config,
              url: retryUrl.pathname + retryUrl.search
            });
          } else {
            // First auth attempt: redirect to Midway
            window.location.href = replaceMidwayAuthNRedirectURL(loginURL);
          }
      } else {
        console.error("unexpected error when requesting web server:", error);
      }
      return Promise.reject(error);
    },
  );
  return webServerAxiosInstance;
};

export const getGatewayBackendAxiosInstance =  () => {
  const gatewayBackendAxiosInstance = axios.create({
    baseURL: window.sancus.env.backendBaseUrl,
    timeout: 25000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  gatewayBackendAxiosInstance.interceptors.request.use((config) => config, (error) => Promise.reject(error));

  return gatewayBackendAxiosInstance;
};

export const getCustomAxiosInstance = async (baseUrl: string) => {
  const customBackendAxiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 25000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  customBackendAxiosInstance.interceptors.request.use((config) => config, (error) => Promise.reject(error));

  return customBackendAxiosInstance;
};
